import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WelcomeOnboardingDialog } from '@/components/organisms/WelcomeOnboardingDialog';
import type { WelcomeOnboardingStep } from '@/components/organisms/WelcomeOnboardingDialog';
import { useFeedbackSurveyTrigger } from '@/hooks/useFeedbackSurveyTrigger';
import { NpsScale, CardRadioGroup, CardCheckboxGroup, FeedbackTextarea } from './FeedbackControls';

interface StudentFeedbackSurveyProps {
  /** Si es true (p. ej. onboarding de bienvenida abierto), no abre el cuestionario. */
  suppress?: boolean;
  /** Notifica al padre si el cuestionario está abierto (para suprimir otros drawers). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Cuestionario de feedback del alumno (NPS + valoraciones + abiertas).
 * Reutiliza `WelcomeOnboardingDialog` (slides a pantalla completa) construyendo
 * los `steps` a medida. El backend decide cuándo mostrarlo (≥2 días con
 * actividad, una vez, con reintento único); aquí solo se abre y se envía.
 */
export function StudentFeedbackSurvey({ suppress = false, onOpenChange }: StudentFeedbackSurveyProps) {
  const { t } = useTranslation();
  const { show, markShown, submit } = useFeedbackSurveyTrigger();
  const [searchParams] = useSearchParams();
  // Modo preview temporal (?feedbackPreview=1): fuerza abrir el drawer para
  // validar el look & feel sin tocar el backend (no marca mostrado ni envía).
  const previewMode = searchParams.get('feedbackPreview') === '1';

  const [open, setOpen] = useState(false);
  const openedRef = useRef(false);

  // Respuestas
  const [nps, setNps] = useState<number | null>(null);
  const [npsReason, setNpsReason] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [levelFit, setLevelFit] = useState<string | null>(null);
  const [easeOfUse, setEaseOfUse] = useState<number | null>(null);
  const [topFeatures, setTopFeatures] = useState<string[]>([]);
  const [improvement, setImprovement] = useState('');

  // Abrir una sola vez cuando el backend lo indica (y no está suprimido).
  useEffect(() => {
    if (openedRef.current) return;
    // Preview: abre sin tocar el backend (no marca mostrado ni envía).
    if (previewMode) {
      openedRef.current = true;
      setOpen(true);
      return;
    }
    if (suppress || !show) return;
    openedRef.current = true;
    setOpen(true);
    void markShown();
  }, [previewMode, show, suppress, markShown]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const npsReasonTitle = useMemo(() => {
    if (nps !== null && nps >= 9) return t('feedbackSurvey.npsReason.titlePromoter');
    if (nps !== null && nps <= 6) return t('feedbackSurvey.npsReason.titleDetractor');
    return t('feedbackSurvey.npsReason.titlePassive');
  }, [nps, t]);

  const steps = useMemo<WelcomeOnboardingStep[]>(
    () => [
      {
        id: 'intro',
        title: t('feedbackSurvey.intro.title'),
        description: t('feedbackSurvey.intro.description'),
        cta: {
          primaryLabel: t('feedbackSurvey.intro.primary'),
          ghostLabel: t('feedbackSurvey.intro.ghost'),
        },
      },
      {
        id: 'nps',
        title: t('feedbackSurvey.nps.title'),
        nextDisabled: nps === null,
        content: (
          <NpsScale
            value={nps}
            onChange={setNps}
            lowLabel={t('feedbackSurvey.nps.low')}
            highLabel={t('feedbackSurvey.nps.high')}
          />
        ),
      },
      {
        id: 'npsReason',
        title: npsReasonTitle,
        content: (
          <FeedbackTextarea
            value={npsReason}
            onChange={setNpsReason}
            placeholder={t('feedbackSurvey.npsReason.placeholder')}
          />
        ),
      },
      {
        id: 'progress',
        title: t('feedbackSurvey.progress.title'),
        content: (
          <CardRadioGroup
            value={progress !== null ? String(progress) : null}
            onChange={(v) => setProgress(Number(v))}
            options={[
              { value: '1', label: t('feedbackSurvey.progress.options.none') },
              { value: '2', label: t('feedbackSurvey.progress.options.little') },
              { value: '3', label: t('feedbackSurvey.progress.options.quite') },
              { value: '4', label: t('feedbackSurvey.progress.options.lot') },
            ]}
          />
        ),
      },
      {
        id: 'levelFit',
        title: t('feedbackSurvey.levelFit.title'),
        content: (
          <CardRadioGroup
            value={levelFit}
            onChange={setLevelFit}
            options={[
              { value: 'easy', label: t('feedbackSurvey.levelFit.options.easy') },
              { value: 'right', label: t('feedbackSurvey.levelFit.options.right') },
              { value: 'hard', label: t('feedbackSurvey.levelFit.options.hard') },
            ]}
          />
        ),
      },
      {
        id: 'ease',
        title: t('feedbackSurvey.ease.title'),
        content: (
          <CardRadioGroup
            value={easeOfUse !== null ? String(easeOfUse) : null}
            onChange={(v) => setEaseOfUse(Number(v))}
            options={[
              { value: '1', label: t('feedbackSurvey.ease.options.veryHard') },
              { value: '2', label: t('feedbackSurvey.ease.options.regular') },
              { value: '3', label: t('feedbackSurvey.ease.options.easy') },
              { value: '4', label: t('feedbackSurvey.ease.options.veryEasy') },
            ]}
          />
        ),
      },
      {
        id: 'topFeature',
        title: t('feedbackSurvey.topFeature.title'),
        description: t('feedbackSurvey.topFeature.multiHint'),
        content: (
          <CardCheckboxGroup
            values={topFeatures}
            onToggle={(v) =>
              setTopFeatures((prev) =>
                prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
              )
            }
            options={[
              { value: 'stories', label: t('feedbackSurvey.topFeature.options.stories') },
              { value: 'conversation', label: t('feedbackSurvey.topFeature.options.conversation') },
              { value: 'vocabulary', label: t('feedbackSurvey.topFeature.options.vocabulary') },
              { value: 'gamification', label: t('feedbackSurvey.topFeature.options.gamification') },
              { value: 'rewards', label: t('feedbackSurvey.topFeature.options.rewards') },
            ]}
          />
        ),
      },
      {
        id: 'improvement',
        title: t('feedbackSurvey.improvement.title'),
        content: (
          <FeedbackTextarea
            value={improvement}
            onChange={setImprovement}
            placeholder={t('feedbackSurvey.improvement.placeholder')}
          />
        ),
        // Al avanzar desde la última pregunta, enviamos la respuesta; el dialog
        // muestra el spinner del arrow mientras dura y luego pasa al slide de gracias.
        onNext: async () => {
          if (previewMode) return; // preview: no envía nada
          if (nps === null) return;
          await submit({
            nps,
            npsReason: npsReason.trim() || undefined,
            progress,
            levelFit,
            easeOfUse,
            topFeatures: topFeatures.length > 0 ? topFeatures : undefined,
            improvement: improvement.trim() || undefined,
          });
        },
      },
      {
        id: 'thanks',
        title: t('feedbackSurvey.thanks.title'),
        description: t('feedbackSurvey.thanks.description'),
        cta: {
          primaryLabel: t('feedbackSurvey.thanks.primary'),
          primaryAction: () => setOpen(false),
        },
      },
    ],
    [t, npsReasonTitle, nps, npsReason, progress, levelFit, easeOfUse, topFeatures, improvement, submit, previewMode]
  );

  return (
    <WelcomeOnboardingDialog
      open={open}
      steps={steps}
      allowSwipe={false}
      onComplete={() => setOpen(false)}
      onLater={() => setOpen(false)}
      nextLabel={t('feedbackSurvey.nextAriaLabel')}
      finishLabel={t('feedbackSurvey.finishAriaLabel')}
      paginationLabel={t('feedbackSurvey.paginationLabel')}
      closeLabel={t('feedbackSurvey.closeLabel')}
    />
  );
}

StudentFeedbackSurvey.displayName = 'StudentFeedbackSurvey';

export default StudentFeedbackSurvey;
